"use client";

import { useEffect } from "react";
import { Pencil } from "lucide-react";
import { useEditableForm } from "@/stores/useEditableFormStore";
import { Button } from "@/components/forms/Button";
import { cn } from "@/lib/utils/cn";

export interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  ref?: React.Ref<HTMLFormElement>;
  editable?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  editButton?: boolean;
  editButtonPosition?: "top-right" | "bottom-right";
  children: React.ReactNode;
}

export function Form({
  ref,
  editable = false,
  onEdit,
  onCancel,
  editButton = false,
  editButtonPosition = "top-right",
  children,
  className = "",
  ...props
}: FormProps) {
  const { isEditing, startEditing, setEditableMode } = useEditableForm();

  useEffect(() => {
    setEditableMode(editable);
    return () => setEditableMode(false);
  }, [editable, setEditableMode]);

  const handleEdit = () => {
    startEditing();
    onEdit?.();
  };

  return (
    <div className="relative">
      {editable && editButton && !isEditing && (
        <Button
          type="button"
          iconOnly
          startIcon={<Pencil className="w-4 h-4 text-gray-600" />}
          variant="white"
          size="md"
          onClick={handleEdit}
          title="Edit"
          aria-label="Edit form"
          className={cn(
            "absolute z-10",
            editButtonPosition === "top-right"
              ? "top-0 right-0"
              : "bottom-0 right-0"
          )}
        />
      )}

      <form ref={ref} className={className} {...props}>
        {children}
      </form>
    </div>
  );
}

export interface FormEditButtonProps {
  position?: "top-right" | "bottom-right" | "inline";
  className?: string;
  formId?: string;
}

export function FormEditButton({
  position = "top-right",
  className = "",
  formId,
}: FormEditButtonProps) {
  const {
    forms,
    isEditing: globalIsEditing,
    editableMode: globalEditableMode,
    startEditing,
  } = useEditableForm();

  const isEditing = formId
    ? forms[formId]?.isEditing ?? false
    : globalIsEditing;
  const editableMode = formId
    ? forms[formId]?.editableMode ?? false
    : globalEditableMode;

  if (!editableMode || isEditing) return null;

  const positionClasses =
    position === "inline"
      ? ""
      : cn(
          "absolute z-10",
          position === "top-right" ? "top-0 right-0" : "bottom-0 right-0"
        );

  const handleClick = () => {
    startEditing(formId);
  };

  return (
    <Button
      type="button"
      iconOnly
      startIcon={<Pencil className="w-4 h-4 text-gray-600" />}
      variant="white"
      size="md"
      onClick={handleClick}
      title="Edit"
      aria-label="Edit form"
      className={cn(positionClasses, className)}
    />
  );
}

export interface FormActionsWrapperProps {
  children: React.ReactNode;
  className?: string;
  formId?: string;
}

export function FormActionsWrapper({
  children,
  className = "",
  formId,
}: FormActionsWrapperProps) {
  const {
    forms,
    isEditing: globalIsEditing,
    editableMode: globalEditableMode,
  } = useEditableForm();

  const isEditing = formId
    ? forms[formId]?.isEditing ?? false
    : globalIsEditing;

  const editableMode = formId
    ? forms[formId]?.editableMode ?? false
    : globalEditableMode;

  if (editableMode && !isEditing) return null;

  return <div className={cn("mt-6", className)}>{children}</div>;
}
